import { useCallback, useMemo, useRef } from 'react';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import {
  UserSettingId,
  UserSettingMappedValue,
} from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { useCreateUserSettingValue } from './use-create-user-setting-value';
import { useUpdateUserSettingValue } from './use-update-user-setting-value';

interface UseUserSettingOptions<T> {
  valueType: string;
  decode: (raw: UserSettingMappedValue | undefined) => T;
  encode: (next: T) => string;
}

export function useUserSetting<T>(
  id: UserSettingId,
  { valueType, decode, encode }: UseUserSettingOptions<T>,
): readonly [T, (next: T) => void] {
  const { user } = useCurrentUserProfile();
  const { createUserSettingValue } = useCreateUserSettingValue();
  const { updateUserSettingValue } = useUpdateUserSettingValue();

  const setting = user?.userSettingValueMap.get(id);

  // callers typically pass `encode`/`decode` as inline arrows, so reading them
  // through refs keeps `setValue` stable and `value` referentially stable
  // (decoded objects feed consumer memo chains) without forcing the caller to
  // memoize. `decode` must therefore be a pure function of its argument — the
  // memo only re-runs when the stored value changes, not when `decode` does.
  const decodeRef = useRef(decode);
  decodeRef.current = decode;

  const settingValue = setting?.value;
  const value = useMemo(() => decodeRef.current(settingValue), [settingValue]);

  const encodeRef = useRef(encode);
  encodeRef.current = encode;

  const setValue = useCallback(
    (next: T) => {
      const serialized = encodeRef.current(next);
      if (setting) {
        updateUserSettingValue(setting.userSettingValue, { value: serialized });
      } else {
        createUserSettingValue({ userSettingId: id, value: serialized, valueType });
      }
    },
    [createUserSettingValue, id, setting, updateUserSettingValue, valueType],
  );

  return [value, setValue] as const;
}
